import * as React from 'react';
import {
  Alert,
  Button,
  callStatusLabel,
  EmptyState,
  Inline,
  Loading,
  MobileHeader,
  MobileScreen,
  Stack,
  toneForStatus,
} from '@relay-towing/ui';
import { Plus } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAcceptOutsideCall } from '../../hooks/useAcceptOutsideCall';
import { useDriverCall } from '../../hooks/useDriverCall';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import { useMember } from '../../hooks/useMember';
import { isUnderWay } from '../../lib/callStatus';
import { seesOfficeHome } from '../../lib/driverAppTabs';
import { isBlankVehicle } from '../../lib/vehicleSchema';
import type { Adder } from '../shell/adder';
import { useBack } from '../shell/useBack';
import { CallAddSheet, type CallAddition } from './CallAddSheet';
import { CallDetailContent, type CallAdders } from './CallDetailContent';
import { CallStatusControl } from './CallStatusControl';
import type { CallRecord } from './callRecord';
import { useStartCall } from './useStartCall';

/** Why taking an outside call failed, in the driver's words. */
const acceptFailure = (error: unknown) => {
  const { status, message } = error as { status?: number; message?: string };
  if (status === 409) return 'Someone else took this call first.';
  if (status === 403 || status === 404)
    return 'This call is no longer one you can take.';
  return message
    ? `The call could not be accepted: ${message}`
    : 'The call could not be accepted.';
};

/**
 * One call, as the Figma's no-map frame draws it (344:65042): the header
 * along the top, then what the call says (CallDetailContent), with its
 * status at the foot, for everyone. It was a sheet over a full-bleed map
 * (344:65534) whenever maps worked and the call had a pickup to pin, so the
 * same tap opened a map for one member and the call for another, by
 * whether their calls carried a pickup point; the owner asked for the call
 * itself for all (2026-10-27). The map is the Calls tab's and Drivers'.
 * Opened from the list, a pin, the active-call pill or a push.
 *
 * An outside call on offer, opened from the offers at the top of the calls
 * list by a driver who may accept outside calls, has "Accept and take it"
 * at its foot instead: it takes the call with them as its driver, and the
 * call is theirs once the motor club confirms it. The server decides who may; this only hides the button from
 * a role that may not.
 */
export function CallDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBack('/calls');
  const member = useMember();
  const { data: profile } = useDriverProfile();
  const query = useDriverCall(id);
  const call = query.data as unknown as CallRecord | undefined;
  const { start, starting, error: startError } = useStartCall();
  const acceptOffer = useAcceptOutsideCall();
  const [acceptError, setAcceptError] = React.useState<string | null>(null);
  // The call's Add and the sections it reaches (see `additions`).
  const [adding, setAdding] = React.useState(false);
  const adders: CallAdders = {
    photos: React.useRef<Adder>(null),
    vehicle: React.useRef<Adder>(null),
    note: React.useRef<Adder>(null),
  };

  if (query.isLoading || (!call && !query.isError))
    return <Loading label="Loading the call" fill="screen" />;
  if (!call)
    return (
      <MobileScreen
        header={<MobileHeader onBack={back} backLabel="Back" title="Call" />}
      >
        <EmptyState
          title="This call is not available"
          description="It may have been deleted, or it is not one your role can see."
          action={<Button onClick={() => navigate('/calls')}>All calls</Button>}
        />
      </MobileScreen>
    );

  const onCall =
    !!profile &&
    (call.drivers ?? []).some((driver) => driver.id === profile.id);
  const editable = onCall || member.can('calls.edit');
  // A driver on the call may cancel it or report it gone on arrival;
  // calls.cancel lets the office, as the server has it (canCancelCall).
  const cancels = onCall || member.can('calls.cancel');
  const takesOffer =
    !!profile &&
    !onCall &&
    call.status === 'OFFERED' &&
    (call.drivers ?? []).length === 0 &&
    member.can('calls.accept_outside');
  // Without "View all invoices" the server lets a member bill and take
  // payment only on a call assigned to them: a driver given these keys is
  // paid on the spot for a vehicle left at the customer's. Never on an
  // Urgently call, which Urgently pays for, as the old screen had it.
  const bills =
    (onCall || member.can('invoices.view_all')) && call.source !== 'URGENTLY';
  const createsInvoice =
    bills && !call.invoice && member.can('invoices.create');
  // Whoever made the invoice can open it again, as on the old screen, which
  // says an owner or admin collects it when they cannot.
  const invoiceAction =
    bills && call.invoice
      ? member.can('invoices.take_payment')
        ? 'take'
        : member.can('invoices.create')
          ? 'view'
          : null
      : null;
  // The vehicle's change history is the office's: someone who only drives
  // has their own day to get on with (the owner, 2026-10-27). The server
  // still answers a member who asks; this only takes the button away.
  const showsActivity = seesOfficeHome(member);

  // The call's Add, beside its status while it is under way (Figma
  // 721-23588): the photos, vehicle and note sections' own add sheets, in
  // one place along the bottom. Photos and notes hang off the vehicle's row,
  // and a vehicle already filled in is changed from its card instead.
  const vehicleId = call.vehicle?.id;
  const additions: CallAddition[] =
    editable && vehicleId && isUnderWay(call.status)
      ? [
          'photos',
          ...(isBlankVehicle(call.vehicle) ? (['vehicle'] as const) : []),
          'note',
        ]
      : [];

  const take = async () => {
    if (!profile) return;
    setAcceptError(null);
    try {
      const { dispatch } = await acceptOffer.mutateAsync({
        callId: call.id,
        driverId: profile.id,
      });
      // Urgently answered that the job is gone, and the call was cancelled.
      if (dispatch?.status === 'CANCELED') {
        setAcceptError('The job was withdrawn before it could be accepted.');
      }
    } catch (error) {
      setAcceptError(acceptFailure(error));
    }
  };

  const header = (
    <MobileHeader
      onBack={back}
      backLabel="Back to calls"
      status={{
        label: callStatusLabel(call.status),
        tone: toneForStatus(call.status),
        since: call.towingAt ?? call.arrivedAt ?? call.startedAt ?? undefined,
      }}
    />
  );
  const content = (
    <CallDetailContent
      call={call}
      editable={editable}
      cancels={cancels}
      createsInvoice={createsInvoice}
      invoiceAction={invoiceAction}
      showsActivity={showsActivity}
      adders={adders}
    />
  );
  const foot = (
    <Stack gap={2} grow>
      {startError ? <Alert tone="destructive">{startError}</Alert> : null}
      {acceptError ? <Alert tone="destructive">{acceptError}</Alert> : null}
      {takesOffer ? (
        <Button fullWidth loading={acceptOffer.isPending} onClick={take}>
          Accept and take it
        </Button>
      ) : (
        <Inline gap={4} align="center" wrap={false}>
          {additions.length > 0 ? (
            <>
              <Button
                variant="secondary"
                size="xl"
                pill
                leadingIcon={<Plus />}
                onClick={() => setAdding(true)}
              >
                Add
              </Button>
              <CallAddSheet
                open={adding}
                onOpenChange={setAdding}
                offers={additions}
                onPick={(addition) => adders[addition].current?.add()}
              />
            </>
          ) : null}
          <Stack gap={2} grow>
            <CallStatusControl
              call={call}
              onCall={onCall}
              onStart={() => start(call)}
              starting={starting}
              onCompleted={() => navigate('/calls', { replace: true })}
              onImpounded={(impoundId) =>
                navigate(`/impounds/${impoundId}`, { replace: true })
              }
            />
          </Stack>
        </Inline>
      )}
    </Stack>
  );

  return (
    <MobileScreen header={header} footer={foot}>
      {content}
    </MobileScreen>
  );
}
